import React from "react";
import { createPortal } from "react-dom";
import styled, { keyframes } from "styled-components";
import { countries } from "country-data-list";
import { FiArrowLeft, FiCheck, FiChevronRight, FiPlus, FiSearch, FiX } from "react-icons/fi";
import { BrandLinkedIn } from "./ui/icons";
import CompanyPicker from "./CompanyPicker";
import CompanyLogo from "./CompanyLogo";
import { PageScrollLock } from "../utils/useLockPageScroll";
import { errorText, fetchWithAuth } from "../utils/authFetch";
import { creditsChanged } from "../utils/useCredits";

/* Find contacts: pick who you're after, check the titles, company and country, and the people
   there now are listed right here. No search is recorded; Save keeps one person as a contact. */

const AUDIENCES = [
  {
    id: "csuite",
    title: "C-suite",
    blurb: "",
    titles: [
      "Chief Executive Officer",
      "CEO",
      "Chief Financial Officer",
      "CFO",
      "Chief Operating Officer",
      "Chief Technology Officer",
      "Chief Marketing Officer",
      "Managing Director",
      "Founder",
    ],
  },
  {
    id: "hr",
    title: "HR & Talent Acquisition",
    blurb: "",
    titles: [
      "Chief People Officer",
      "Head of HR",
      "HR Director",
      "Head of Talent Acquisition",
      "Talent Acquisition",
      "Recruiter",
      "HR Business Partner",
      "HR Manager",
    ],
  },
  {
    id: "other",
    title: "Someone else",
    blurb: "Type your own job titles, e.g. Head of Sales.",
    titles: [],
  },
];
const MAX_TITLES = 12;

const COUNTRY_OPTIONS = countries.all
  .filter((c) => c?.name && c?.alpha2 && c?.status !== "deleted" && c?.ioc !== "PRK")
  .sort((a, b) => a.name.localeCompare(b.name));

// "Hong Kong" / "hk" / "United States" -> "hk" / "us"; "" when it isn't a country we know.
function countryCode(value) {
  const v = String(value || "").trim().toLowerCase();
  if (!v) return "";
  const hit = COUNTRY_OPTIONS.find((c) => c.alpha2.toLowerCase() === v || c.name.toLowerCase() === v);
  if (hit) return hit.alpha2.toLowerCase();
  const loose = COUNTRY_OPTIONS.find((c) => c.name.toLowerCase().startsWith(v) || v.startsWith(c.name.toLowerCase()));
  return loose ? loose.alpha2.toLowerCase() : "";
}

function newSearchId() {
  try {
    return crypto.randomUUID();
  } catch {
    return `${Date.now().toString(16)}-${Math.random().toString(16).slice(2, 10)}`;
  }
}

const initialsOf = (name) =>
  String(name || "")
    .split(/\s+/)
    .filter(Boolean)
    .map((s) => s[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);

const squash = (v) => String(v || "").replace(/\s+/g, " ").trim();
const nameKey = (v) => squash(v).toLowerCase().replace(/[^a-z0-9]+/g, "");

// Errors that every remaining title would hit too, so the search stops at the first one.
const STOP_CODES = new Set(["insufficient_credits", "company_not_found", "no_company", "unavailable"]);

async function readError(res, fallback) {
  try {
    const body = await res.json();
    return { message: body?.error || fallback, code: res.status === 402 ? "insufficient_credits" : body?.code || "" };
  } catch {
    return { message: fallback, code: res.status === 402 ? "insufficient_credits" : "" };
  }
}

/* `company` / `country` prefill the filters; `onSaved` runs after a contact is saved or removed. */
export default function FindContactsModal({ open, onClose, company = "", country = "", onSaved }) {
  const [step, setStep] = React.useState("audience"); // audience | filters | results
  const [audience, setAudience] = React.useState(null);
  const [titles, setTitles] = React.useState([]);
  const [titleDraft, setTitleDraft] = React.useState("");
  const [companyName, setCompanyName] = React.useState("");
  const [cc, setCc] = React.useState("");
  const [error, setError] = React.useState("");
  const [rows, setRows] = React.useState([]);
  const [progress, setProgress] = React.useState(null); // { done, total, current } while searching
  const [searched, setSearched] = React.useState({ shown: "", saveUnder: "" }); // the company of the results on screen
  const [saved, setSaved] = React.useState(() => new Set());
  const [busy, setBusy] = React.useState(() => new Set());
  const runRef = React.useRef(0);

  // Each opening starts fresh, prefilled from the page behind it; prop changes while open leave the user's edits alone.
  const wasOpen = React.useRef(false);
  React.useEffect(() => {
    if (open && !wasOpen.current) {
      runRef.current += 1;
      setStep("audience");
      setAudience(null);
      setTitles([]);
      setTitleDraft("");
      setCompanyName(squash(company));
      setCc(countryCode(country));
      setError("");
      setRows([]);
      setProgress(null);
      setSearched({ shown: "", saveUnder: "" });
      setSaved(new Set());
      setBusy(new Set());
    }
    wasOpen.current = open;
  }, [open, company, country]);

  // Closing (or going back) stops a search still going: its later titles are not searched.
  const stopRun = () => {
    runRef.current += 1;
    setProgress(null);
  };
  const close = React.useCallback(() => {
    runRef.current += 1;
    onClose?.();
  }, [onClose]);

  React.useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key !== "Escape" || e.target?.getAttribute?.("aria-expanded") === "true") return; // an open dropdown closes first
      e.stopPropagation(); // the page behind keeps its own Escape (e.g. an open profile pane)
      close();
    };
    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
  }, [open, close]);

  const pickAudience = (a) => {
    setAudience(a.id);
    setTitles(a.titles);
    setTitleDraft("");
    setError("");
    setStep("filters");
  };

  // Adds what is typed; a pasted "CEO, CFO" becomes two titles.
  const addTitles = (raw) => {
    const parts = String(raw || "").split(/[,;\n]/).map(squash).filter(Boolean);
    if (!parts.length) return;
    const next = [...titles];
    let problem = "";
    for (const t of parts) {
      if (t.length < 3) problem = "A job title needs at least 3 characters.";
      else if (next.some((x) => x.toLowerCase() === t.toLowerCase())) continue;
      else if (next.length >= MAX_TITLES) problem = `Up to ${MAX_TITLES} job titles at a time.`;
      else next.push(t);
    }
    setTitles(next);
    setTitleDraft("");
    setError(problem);
  };

  const runSearch = async () => {
    const name = squash(companyName);
    if (name.length < 2) return setError("Type the company to search.");
    if (titles.length === 0) return setError("Add at least one job title.");
    // Saved contacts are filed under the page's company when this is that company (or a longer form of it),
    // so they show on its Contacts card; another company typed in is filed under its own name.
    const pageCompany = squash(company);
    const saveUnder = pageCompany && nameKey(name).includes(nameKey(pageCompany)) ? pageCompany : name;
    const run = ++runRef.current;
    const searchId = newSearchId();
    const queue = [...titles];
    setError("");
    setRows([]);
    setSaved(new Set());
    setSearched({ shown: name, saveUnder });
    setStep("results");

    // Who is already saved there, so they show as Saved.
    fetchWithAuth(`/api/outreach/contacts?company=${encodeURIComponent(saveUnder)}`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => {
        if (runRef.current !== run || !Array.isArray(d?.contacts)) return;
        const ids = d.contacts.map((c) => String(c?._id || "")).filter(Boolean);
        setSaved((prev) => new Set([...prev, ...ids]));
      })
      .catch(() => {});

    const seen = new Set();
    let failures = 0;
    let lastError = "";
    try {
      for (let i = 0; i < queue.length; i += 1) {
        if (runRef.current !== run) return;
        setProgress({ done: i, total: queue.length, current: queue[i] });
        let res;
        try {
          res = await fetchWithAuth("/api/contacts/search", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ company: name, title: queue[i], country_code: cc, search_id: searchId }),
          });
        } catch (err) {
          failures += 1;
          lastError = errorText(err, "Could not search right now. Please try again.");
          continue;
        }
        if (runRef.current !== run) return;
        if (!res.ok) {
          const { message, code } = await readError(res, "Could not search right now. Please try again.");
          if (runRef.current !== run) return;
          if (STOP_CODES.has(code)) {
            setError(message);
            return;
          }
          failures += 1;
          lastError = message;
          continue;
        }
        const body = await res.json().catch(() => ({}));
        if (runRef.current !== run) return;
        if (body.company) setSearched((prev) => ({ ...prev, shown: body.company }));
        const fresh = (Array.isArray(body.rows) ? body.rows : []).filter((r) => {
          const key = String(r.username || r.object_id || "").toLowerCase();
          if (!key || seen.has(key)) return false;
          seen.add(key);
          return true;
        });
        if (fresh.length) setRows((prev) => [...prev, ...fresh.map((r) => ({ ...r, matched: queue[i] }))]);
      }
      if (failures === queue.length) setError(lastError);
      else if (failures) setError(`${failures} of ${queue.length} titles couldn't be searched just now.`);
    } finally {
      if (runRef.current === run) setProgress(null);
      creditsChanged(); // every title searched was charged, including a search cut short
    }
  };

  const toggleSave = async (row) => {
    const id = row.object_id;
    if (!id || busy.has(id) || !searched.saveUnder) return;
    const on = saved.has(id);
    const flip = (set, add) => {
      const next = new Set(set);
      if (add) next.add(id);
      else next.delete(id);
      return next;
    };
    setBusy((b) => flip(b, true));
    setSaved((s) => flip(s, !on));
    try {
      const res = on
        ? await fetchWithAuth(`/api/outreach/external-shortlist/${encodeURIComponent(id)}`, { method: "DELETE" })
        : await fetchWithAuth("/api/outreach/external-shortlist", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ objectId: id, contactCompany: searched.saveUnder }),
          });
      if (!res.ok) throw new Error((await readError(res, "Couldn't save this person.")).message);
      onSaved?.();
    } catch (err) {
      setSaved((s) => flip(s, on));
      setError(errorText(err, "Couldn't save this person."));
    } finally {
      setBusy((b) => flip(b, false));
    }
  };

  if (!open) return null;
  const searching = Boolean(progress);
  const aud = AUDIENCES.find((a) => a.id === audience);
  const canSearch = titles.length > 0 && squash(companyName).length >= 2;
  const savedHere = rows.filter((r) => saved.has(r.object_id)).length;
  const headCompany = squash(step === "results" ? searched.shown : companyName);

  return createPortal(
    <Backdrop onMouseDown={(e) => e.target === e.currentTarget && close()}>
      <PageScrollLock />
      <Dialog role="dialog" aria-modal="true" aria-label="Find contacts">
        <Head>
          <HeadLeft>
            {step !== "audience" && (
              <IconBtn
                type="button"
                aria-label="Back"
                onClick={() => {
                  stopRun();
                  setError("");
                  setStep(step === "results" ? "filters" : "audience");
                }}
              >
                <FiArrowLeft size={16} />
              </IconBtn>
            )}
            {headCompany && <CompanyLogo name={headCompany} size={36} radius={8} />}
            <div>
              <h3>{headCompany ? `Find contacts at ${headCompany}` : "Find contacts"}</h3>
              <Sub>
                {step === "audience"
                  ? "Start from a set of job titles. You can change them next."
                  : step === "filters"
                    ? aud?.title || ""
                    : aud?.title || "People"}
              </Sub>
            </div>
          </HeadLeft>
          <IconBtn type="button" aria-label="Close" onClick={close}>
            <FiX size={18} />
          </IconBtn>
        </Head>
        <Track aria-hidden="true">
          {searching && <Fill style={{ width: `${Math.max(4, (progress.done / progress.total) * 100)}%` }} />}
        </Track>

        <Body>
          {step === "audience" && (
            <Options>
              {AUDIENCES.map((a) => (
                <Option key={a.id} type="button" onClick={() => pickAudience(a)}>
                  <OptionText>
                    <OptionTitle>
                      {a.title}
                      {a.titles.length > 0 && <Count>{a.titles.length} titles</Count>}
                    </OptionTitle>
                    {a.blurb && <OptionBlurb>{a.blurb}</OptionBlurb>}
                    {a.titles.length > 0 && (
                      <Tags>
                        {a.titles.map((t) => (
                          <Tag key={t}>{t}</Tag>
                        ))}
                      </Tags>
                    )}
                  </OptionText>
                  <Arrow size={16} aria-hidden="true" />
                </Option>
              ))}
            </Options>
          )}

          {step === "filters" && (
            <Form
              id="fc-form"
              onSubmit={(e) => {
                e.preventDefault();
                if (titleDraft.trim()) addTitles(titleDraft);
                else runSearch();
              }}
            >
              <Field>
                <Label htmlFor="fc-titles">Job titles</Label>
                <Chips>
                  {titles.map((t) => (
                    <Chip key={t}>
                      {t}
                      <button type="button" aria-label={`Remove ${t}`} onClick={() => setTitles((prev) => prev.filter((x) => x !== t))}>
                        <FiX size={11} />
                      </button>
                    </Chip>
                  ))}
                  <ChipInput
                    id="fc-titles"
                    value={titleDraft}
                    autoFocus={titles.length === 0}
                    placeholder={titles.length ? "Add a title" : "e.g. Head of Sales"}
                    onChange={(e) => setTitleDraft(e.target.value)}
                    onPaste={(e) => {
                      const text = e.clipboardData.getData("text");
                      if (!/[,;\n]/.test(text)) return;
                      e.preventDefault();
                      addTitles(titleDraft + text);
                    }}
                    onKeyDown={(e) => {
                      if (e.key === ",") {
                        e.preventDefault();
                        addTitles(titleDraft);
                      } else if (e.key === "Backspace" && !titleDraft && titles.length) {
                        setTitles((prev) => prev.slice(0, -1));
                      }
                    }}
                    onBlur={() => titleDraft.trim() && addTitles(titleDraft)}
                  />
                </Chips>
                <Note>Each title is searched on its own. Remove any you don't need.</Note>
              </Field>
              <Row2>
                <Field>
                  <Label htmlFor="fc-company">Company</Label>
                  <CompanyPicker id="fc-company" input={Input} value={companyName} onChange={setCompanyName} placeholder="Company name" />
                </Field>
                <Field>
                  <Label htmlFor="fc-country">Country</Label>
                  <Select id="fc-country" value={cc} onChange={(e) => setCc(e.target.value)}>
                    <option value="">Any country</option>
                    {COUNTRY_OPTIONS.map((c) => (
                      <option key={c.alpha2} value={c.alpha2.toLowerCase()}>
                        {c.name}
                      </option>
                    ))}
                  </Select>
                </Field>
              </Row2>
              {error && <ErrorText>{error}</ErrorText>}
            </Form>
          )}

          {step === "results" && (
            <>
              <Status>
                {searching ? (
                  <>
                    Searching “{progress.current}” · {progress.done + 1} of {progress.total}
                    {rows.length > 0 && ` · ${rows.length} found`}
                  </>
                ) : (
                  `${rows.length} ${rows.length === 1 ? "person" : "people"} found`
                )}
              </Status>
              {error && <ErrorText>{error}</ErrorText>}
              {!searching && rows.length === 0 && !error && (
                <Empty>Nobody there under those titles right now. Go back and try other titles or another country.</Empty>
              )}
              <List>
                {rows.map((r) => {
                  const id = r.object_id;
                  const on = saved.has(id);
                  const sub = r.title ? `${r.title}${r.company ? ` at ${r.company}` : ""}` : r.headline;
                  return (
                    <Person key={r.username || id}>
                      <Face>
                        {r.has_photo && r.username ? (
                          <img src={`/api/candidate-photo/${encodeURIComponent(String(r.username).toLowerCase())}`} alt="" />
                        ) : (
                          initialsOf(r.name) || "?"
                        )}
                      </Face>
                      <PersonText>
                        <Name href={r.link || undefined} target="_blank" rel="noopener noreferrer">
                          {r.name}
                          {r.link && <BrandLinkedIn size={12} />}
                        </Name>
                        {sub && <Meta>{sub}</Meta>}
                        <Meta $faint>{[r.location || r.country, `Found as “${r.matched}”`].filter(Boolean).join(" · ")}</Meta>
                      </PersonText>
                      {id && (
                        <SaveBtn
                          type="button"
                          $on={on}
                          disabled={busy.has(id)}
                          onClick={() => toggleSave(r)}
                          title={on ? `Saved to ${searched.saveUnder} contacts. Click to remove` : `Save to ${searched.saveUnder} contacts`}
                        >
                          {on ? <FiCheck size={12} /> : <FiPlus size={12} />}
                          {on ? "Saved" : "Save"}
                        </SaveBtn>
                      )}
                    </Person>
                  );
                })}
              </List>
            </>
          )}
        </Body>

        {step === "filters" && (
          <Foot>
            <FootNote>{titles.length ? `${titles.length} ${titles.length === 1 ? "title" : "titles"}` : ""}</FootNote>
            <PrimaryBtn type="submit" form="fc-form" disabled={!canSearch}>
              <FiSearch size={14} /> Search
            </PrimaryBtn>
          </Foot>
        )}
        {step === "results" && !searching && (
          <Foot>
            <FootNote>{rows.length ? (savedHere ? `${savedHere} saved` : "Save the people you want to keep") : ""}</FootNote>
            <GhostBtn type="button" onClick={() => setStep("filters")}>
              Change filters
            </GhostBtn>
          </Foot>
        )}
      </Dialog>
    </Backdrop>,
    document.body,
  );
}

/* ─── Styles ─── */

const popIn = keyframes`
  from { opacity: 0; transform: translateY(8px); }
  to { opacity: 1; transform: none; }
`;

const Backdrop = styled.div`
  position: fixed;
  inset: 0;
  z-index: 3000;
  background: var(--scrim, rgba(0, 0, 0, 0.45));
  display: grid;
  place-items: center;
  padding: 24px;
  @media (max-width: 640px) {
    padding: 12px;
  }
`;

/* Fixed height: the dialog keeps its size across steps instead of jumping, and results get the room. */
const Dialog = styled.div`
  width: 100%;
  max-width: 760px;
  height: min(880px, calc(100dvh - 48px));
  display: flex;
  flex-direction: column;
  overflow: hidden;
  border-radius: 14px;
  background: ${({ theme }) => theme.surface};
  border: 1px solid ${({ theme }) => theme.line};
  color: ${({ theme }) => theme.ink};
  box-shadow: var(--shadow-lg, 0 30px 80px -30px rgba(0, 0, 0, 0.45));
  animation: ${popIn} 0.18s ease-out both;
  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
  @media (max-width: 640px) {
    height: calc(100dvh - 24px);
    border-radius: 12px;
  }
`;

const Head = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 18px 24px 16px;
  & h3 {
    margin: 0;
    font-family: var(--font-display);
    font-weight: 400;
    font-size: 24px;
    line-height: 1.15;
  }
`;

const HeadLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
`;

const Sub = styled.div`
  font-size: 13.5px;
  color: var(--ink-soft);
  margin-top: 3px;
`;

/* Hairline under the header; doubles as the search progress bar. */
const Track = styled.div`
  flex: none;
  height: 2px;
  background: var(--line-soft);
`;

const Fill = styled.div`
  height: 100%;
  background: ${({ theme }) => theme.ink};
  transition: width 0.4s ease;
`;

const IconBtn = styled.button`
  display: grid;
  place-items: center;
  flex: none;
  width: 32px;
  height: 32px;
  border: 0;
  border-radius: 8px;
  background: transparent;
  color: var(--ink-soft);
  cursor: pointer;
  &:hover {
    background: var(--surface-tint);
    color: ${({ theme }) => theme.ink};
  }
`;

const Body = styled.div`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding: 20px 24px 24px;
`;

/* Hover and press timings (tasteskill motion): 140ms in, 200ms out, 90ms press, one easing. */
const EASE = "cubic-bezier(.2, .8, .2, 1)";
const pressable = `
  transition: transform 200ms ${EASE}, background-color 200ms ${EASE}, border-color 200ms ${EASE}, color 200ms ${EASE};
  &:focus-visible {
    outline: 2px solid var(--accent);
    outline-offset: 2px;
  }
  @media (hover: hover) and (pointer: fine) {
    &:hover:not(:disabled) {
      transform: translateY(-1px);
      transition-duration: 140ms;
    }
  }
  &:active:not(:disabled) {
    transform: translateY(1px) scale(0.985);
    transition-duration: 90ms;
  }
  @media (prefers-reduced-motion: reduce) {
    &, &:hover:not(:disabled), &:active:not(:disabled) {
      transform: none;
    }
  }
`;

const Options = styled.div`
  display: grid;
  gap: 10px;
`;

const Arrow = styled(FiChevronRight)`
  flex: none;
  transition: transform 200ms ${EASE};
`;

/* A linked card: lifts 2px, its border darkens a step, the arrow moves 3px. */
const Option = styled.button`
  display: flex;
  align-items: center;
  gap: 16px;
  width: 100%;
  padding: 18px 20px;
  text-align: left;
  font: inherit;
  color: var(--ink-faint);
  background: ${({ theme }) => theme.surface};
  border: 1px solid ${({ theme }) => theme.line};
  border-radius: 10px;
  cursor: pointer;
  transition: transform 200ms ${EASE}, border-color 200ms ${EASE}, box-shadow 200ms ${EASE}, color 200ms ${EASE};
  &:focus-visible {
    outline: 2px solid var(--accent);
    outline-offset: 2px;
  }
  @media (hover: hover) and (pointer: fine) {
    &:hover {
      transform: translateY(-2px);
      border-color: var(--ink-faint);
      box-shadow: 0 10px 24px -14px rgba(0, 0, 0, 0.18);
      color: ${({ theme }) => theme.ink};
      transition-duration: 180ms, 140ms, 180ms, 140ms;
    }
    &:hover ${Arrow} {
      transform: translateX(3px);
      transition-duration: 140ms;
    }
  }
  &:active {
    transform: translateY(0) scale(0.995);
    transition-duration: 90ms;
  }
  @media (prefers-reduced-motion: reduce) {
    &, &:hover, &:active, &:hover ${Arrow} {
      transform: none;
    }
  }
`;

const OptionText = styled.div`
  flex: 1;
  min-width: 0;
`;

const OptionTitle = styled.div`
  display: flex;
  align-items: baseline;
  gap: 10px;
  font-weight: 600;
  font-size: 15px;
  color: ${({ theme }) => theme.ink};
`;

const Count = styled.span`
  font-weight: 400;
  font-size: 12.5px;
  color: var(--ink-faint);
`;

const OptionBlurb = styled.div`
  font-size: 13px;
  color: var(--ink-soft);
  margin-top: 4px;
  line-height: 1.45;
`;

/* The same tags the next step lets you edit, so the card shows exactly what will be searched. */
const Tags = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 12px;
`;

const Tag = styled.span`
  display: inline-flex;
  align-items: center;
  height: 24px;
  padding: 0 8px;
  font-size: 12px;
  border-radius: 6px;
  border: 1px solid ${({ theme }) => theme.line};
  background: var(--surface-tint);
  color: var(--ink-soft);
`;

const Form = styled.form`
  display: grid;
  gap: 22px;
`;

const Field = styled.div`
  display: grid;
  gap: 7px;
  min-width: 0;
`;

const Label = styled.label`
  font-size: 13px;
  font-weight: 600;
  color: ${({ theme }) => theme.ink};
`;

const Note = styled.span`
  font-size: 12px;
  color: var(--ink-faint);
`;

const Row2 = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 14px;
  @media (max-width: 560px) {
    grid-template-columns: 1fr;
  }
`;

const fieldBox = `
  height: 40px;
  padding: 0 12px;
  font: inherit;
  font-size: 13.5px;
  border-radius: 8px;
  outline: none;
`;

const Input = styled.input`
  ${fieldBox}
  color: ${({ theme }) => theme.ink};
  background: ${({ theme }) => theme.surface};
  border: 1px solid ${({ theme }) => theme.line};
  &:focus {
    border-color: ${({ theme }) => theme.ink};
  }
`;

const Select = styled.select`
  ${fieldBox}
  color: ${({ theme }) => theme.ink};
  background: ${({ theme }) => theme.surface};
  border: 1px solid ${({ theme }) => theme.line};
  &:focus {
    border-color: ${({ theme }) => theme.ink};
  }
`;

const Chips = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  min-height: 120px;
  align-content: flex-start;
  padding: 8px;
  border: 1px solid ${({ theme }) => theme.line};
  border-radius: 8px;
  background: ${({ theme }) => theme.surface};
  &:focus-within {
    border-color: ${({ theme }) => theme.ink};
  }
`;

const Chip = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 2px;
  height: 28px;
  padding: 0 4px 0 10px;
  font-size: 12.5px;
  border-radius: 6px;
  border: 1px solid ${({ theme }) => theme.line};
  background: var(--surface-tint);
  color: ${({ theme }) => theme.ink};
  & button {
    display: grid;
    place-items: center;
    width: 20px;
    height: 20px;
    border: 0;
    border-radius: 4px;
    background: transparent;
    color: var(--ink-faint);
    cursor: pointer;
  }
  & button:hover {
    color: ${({ theme }) => theme.ink};
  }
`;

const ChipInput = styled.input`
  flex: 1;
  min-width: 140px;
  height: 28px;
  padding: 0 4px;
  border: 0;
  outline: none;
  font: inherit;
  font-size: 13px;
  background: transparent;
  color: ${({ theme }) => theme.ink};
`;

const ErrorText = styled.div`
  font-size: 13px;
  color: var(--danger);
  margin: 0 0 10px;
`;

/* Pinned to the bottom of the dialog so the action stays in view however long the list gets. */
const Foot = styled.div`
  flex: none;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 14px 24px;
  border-top: 1px solid var(--line-soft);
`;

const FootNote = styled.span`
  font-size: 12.5px;
  color: var(--ink-faint);
`;

const PrimaryBtn = styled.button`
  ${pressable}
  display: inline-flex;
  align-items: center;
  gap: 8px;
  height: 38px;
  padding: 0 18px;
  border: 0;
  border-radius: 8px;
  font: inherit;
  font-size: 13.5px;
  font-weight: 600;
  background: ${({ theme }) => theme.ink};
  color: ${({ theme }) => theme.surface};
  cursor: pointer;
  &:disabled {
    opacity: 0.4;
    cursor: default;
  }
`;

const GhostBtn = styled.button`
  ${pressable}
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 36px;
  padding: 0 14px;
  border-radius: 8px;
  font: inherit;
  font-size: 13px;
  background: transparent;
  color: ${({ theme }) => theme.ink};
  border: 1px solid ${({ theme }) => theme.line};
  cursor: pointer;
  &:hover {
    background: var(--surface-tint);
  }
`;

const Status = styled.div`
  font-size: 13px;
  color: var(--ink-soft);
  margin-bottom: 12px;
`;

const Empty = styled.div`
  padding: 48px 0;
  text-align: center;
  font-size: 13.5px;
  color: var(--ink-soft);
`;

const List = styled.div`
  border-top: 1px solid var(--line-soft);
`;

const Person = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 12px 4px;
  border-bottom: 1px solid var(--line-soft);
`;

const Face = styled.span`
  display: grid;
  place-items: center;
  flex: none;
  width: 40px;
  height: 40px;
  overflow: hidden;
  border-radius: 99px;
  font-size: 12.5px;
  font-weight: 600;
  background: var(--surface-tint);
  color: var(--ink-soft);
  & img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const PersonText = styled.div`
  flex: 1;
  min-width: 0;
`;

const Name = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-weight: 600;
  font-size: 14px;
  color: ${({ theme }) => theme.ink};
  text-decoration: none;
  &:hover {
    text-decoration: underline;
  }
`;

const Meta = styled.div`
  font-size: 12.5px;
  color: ${({ $faint }) => ($faint ? "var(--ink-faint)" : "var(--ink-soft)")};
  margin-top: 2px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const SaveBtn = styled.button`
  ${pressable}
  display: inline-flex;
  align-items: center;
  gap: 5px;
  flex: none;
  height: 30px;
  padding: 0 12px;
  border-radius: 6px;
  font: inherit;
  font-size: 12.5px;
  font-weight: ${({ $on }) => ($on ? 600 : 400)};
  cursor: pointer;
  border: 1px solid ${({ $on, theme }) => ($on ? theme.ink : theme.line)};
  background: ${({ $on, theme }) => ($on ? theme.ink : "transparent")};
  color: ${({ $on, theme }) => ($on ? theme.surface : theme.ink)};
  &:hover:not(:disabled) {
    border-color: ${({ theme }) => theme.ink};
  }
  &:disabled {
    opacity: 0.6;
    cursor: default;
  }
`;
